// Small time helpers shared by the schedule pieces.
// All "hour float" values are UTC-hours: 8.5 == 8:30 in UTC. The rest of
// the FE (convertTimeStampToTime, workday strings) treats stored timestamps
// as wall-clock UTC — see src/utils/timeStampToDate.js. We mirror that
// convention so a service "Booked for 9:00 AM" lands at the 9 AM column
// regardless of the viewer's local timezone.

export const secondsToHourFloat = ts => {
  if (typeof ts !== "number" || !isFinite(ts)) return null;
  const d = new Date(ts * 1000);
  return d.getUTCHours() + d.getUTCMinutes() / 60;
};

export const formatHour = h => {
  if (h == null) return "";
  const hh = Math.floor(h);
  const mm = Math.round((h - hh) * 60);
  const ampm = hh >= 12 ? "PM" : "AM";
  const h12 = ((hh + 11) % 12) + 1;
  return `${h12}:${mm.toString().padStart(2, "0")} ${ampm}`;
};

export const formatHourShort = h => {
  if (h == null) return "";
  const hh = Math.floor(h);
  const mm = Math.round((h - hh) * 60);
  const ampm = hh >= 12 ? "PM" : "AM";
  const h12 = ((hh + 11) % 12) + 1;
  return mm === 0 ? `${h12} ${ampm}` : `${h12}:${mm.toString().padStart(2, "0")} ${ampm}`;
};

export const nowAsHourFloat = (date = new Date()) =>
  date.getUTCHours() + date.getUTCMinutes() / 60 + date.getUTCSeconds() / 3600;

// Shop-local "now" as an hour float, using the branch IANA timezone. This is
// what positions the schedule "now line": services render at their wall-clock
// hour (secondsToHourFloat uses getUTC*), so the shop's CURRENT wall-clock hour
// must sit on that same axis — not the viewer's browser hour. Uses the built-in
// Intl API (no extra dependency). Falls back to UTC hours (previous behaviour)
// when no timezone is known.
export const nowAsShopHourFloat = (timeZone, date = new Date()) => {
  if (!timeZone) return nowAsHourFloat(date);
  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone,
      hour12: false,
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit"
    }).formatToParts(date);
    const get = type => Number(parts.find(p => p.type === type)?.value);
    let h = get("hour");
    if (h === 24) h = 0; // some engines emit '24' at midnight
    return h + get("minute") / 60 + get("second") / 3600;
  } catch {
    return nowAsHourFloat(date);
  }
};

// Produces the visible interval inside the timeline window, clamped to
// [open, close]. Returns null when the interval is fully outside the day.
export const clampToWindow = (start, end, open, close) => {
  if (start == null || end == null) return null;
  const a = Math.max(start, open);
  const b = Math.min(end, close);
  if (b <= a) return null;
  return { start: a, end: b, clampedStart: a > start, clampedEnd: b < end };
};

// Same date in local time?
export const isSameLocalDay = (a, b) =>
  a &&
  b &&
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

// Is `now` the same calendar day (in the shop timezone) as the picked day?
// `selectedDay` is a date-picker Date representing a calendar day, so we compare
// its local Y/M/D against the shop-local Y/M/D of `now`. This gates whether the
// "now line" is drawn (only when viewing today, in shop time). Falls back to a
// plain local-day compare when no timezone is known.
export const isSameShopDay = (now, selectedDay, timeZone) => {
  if (!now || !selectedDay) return false;
  if (!timeZone) return isSameLocalDay(now, selectedDay);
  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit"
    }).formatToParts(now);
    const g = type => Number(parts.find(p => p.type === type)?.value);
    return (
      g("year") === selectedDay.getFullYear() &&
      g("month") === selectedDay.getMonth() + 1 &&
      g("day") === selectedDay.getDate()
    );
  } catch {
    return isSameLocalDay(now, selectedDay);
  }
};
